import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { assets } from '../../data/catalog';
import './admin.css';

const {
  FiGrid, FiPackage, FiShoppingBag, FiBarChart2, FiLogOut, FiExternalLink,
  FiUsers, FiTruck, FiImage, FiMenu, FiX, FiSettings, FiFileText, FiStar, FiTag,
  FiGlobe, FiCreditCard
} = FiIcons;

const ADMIN_EMAIL = 'admin@dekka.lk';

function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const authorize = async (session) => {
      if (!session) {
        setUser(null);
        navigate('/admin/login');
        return;
      }

      if (session.user.email?.toLowerCase() !== ADMIN_EMAIL) {
        setUser(null);
        await supabase.auth.signOut();
        navigate('/admin/login');
        return;
      }

      setUser(session.user);
    };

    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      await authorize(session);
    };

    check();

    const { data: sub } = supabase.auth.onAuthStateChange((_, session) => {
      authorize(session);
    });

    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', label: 'Dashboard', icon: FiGrid, exact: true },
    { to: '/admin/orders', label: 'Orders', icon: FiShoppingBag },
    { to: '/admin/products', label: 'Products', icon: FiPackage },
    { to: '/admin/customers', label: 'Customers', icon: FiUsers },
    { to: '/admin/shipping', label: 'Shipping', icon: FiTruck },
    { to: '/admin/payment-options', label: 'Payment Options', icon: FiCreditCard },
    { to: '/admin/influencers', label: 'Influencers', icon: FiStar },
    { to: '/admin/promo-codes', label: 'Promo Codes', icon: FiTag },
    { to: '/admin/landing', label: 'Landing Content', icon: FiImage },
    { to: '/admin/about-content', label: 'About Content', icon: FiFileText },
    { to: '/admin/analytics', label: 'Shop Analytics', icon: FiBarChart2 },
    { to: '/admin/site-analytics', label: 'Site Analytics', icon: FiGlobe },
    { to: '/admin/settings', label: 'Settings', icon: FiSettings }
  ];

  if (!user) return null;

  return (
    <div className="admin-layout">
      <div className={`admin-sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-brand-logo">
            <img
              src={assets.logo}
              alt="Darshana Global Enterprises"
              style={{ width: '156px', height: '48px', objectFit: 'contain', objectPosition: 'left center' }}
            />
          </div>
          <span className="admin-badge">ADMIN PANEL</span>
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <nav className="admin-nav-links">
          {navItems.map(({ to, label, icon, exact }) => {
            const active = exact ? location.pathname === to : location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} className={`admin-nav-link ${active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                <SafeIcon icon={icon} />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-link" target="_blank"><SafeIcon icon={FiExternalLink} /><span>View Store</span></Link>
          <button className="admin-logout-btn" onClick={handleLogout}>
            <SafeIcon icon={FiLogOut} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
      <div className="admin-content">
        <div className="admin-topbar">
          <button className="admin-menu-toggle" onClick={() => setSidebarOpen(true)}>
            <SafeIcon icon={FiMenu} />
          </button>
          <div className="admin-topbar-left">
            <span className="admin-topbar-title">Store Management</span>
          </div>
          <div className="admin-topbar-user">
            <div className="admin-user-avatar">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <span className="admin-user-email">{user.email}</span>
          </div>
        </div>
        <div className="admin-content-inner">
          {children}
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;